import React, { useEffect } from 'react';
import './CampaignTable.scss';
import { Button, EButtonType, KitIcons } from '@repo/apg-ui';
import { Campaigns } from '../../../containers/Sujet/DashboardV2';
import classNames from 'classnames';
import parse from 'html-react-parser';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactComponent as RedEyeIcon } from '../../../assets/icons/red_eye.svg';
import { ReactComponent as GreenEyeIcon } from '../../../assets/icons/greenEye.svg';
import { ReactComponent as BuyIcon } from '../../../assets/icons/e_i_buy.svg';
import { ReactComponent as CloseXIcon } from '../../../assets/icons/new/closeX.svg';
import { ReactComponent as SaveIcon } from '../../../assets/icons/e_i_save.svg';
import { ReactComponent as CopyIcon } from '../../../assets/icons/copy.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as LockIcon } from '../../../assets/icons/lock.svg';
import { ReactComponent as CheckedIcon } from '../../../assets/icons/check.svg';
import { ReactComponent as ClockIcon } from '../../../assets/icons/new/clock.svg';
import { ReactComponent as UploadIcon } from '../../../assets/icons/e_i_upload.svg';
import { ReactComponent as RedPlusIcon } from '../../../assets/icons/redPlus.svg';

import { formatPrice } from '../../../utils/numbers';
import { format, isValid } from 'date-fns';
import { de } from 'date-fns/locale';
import { isProd, isStage } from '../../../constants/config';
import { isString } from '../../../utils/variableEvaluation';
import { useNavigate } from 'react-router';
import Tooltip from 'rc-tooltip';
import { RootState } from '../../../reducers';
import { bindActionCreators, Dispatch } from 'redux';
import * as actions from '../../../actions';
import * as _actionsSujet from '../../../actions/sujet';
import * as _actionsBasket from '../../../actions/basket';
import * as _actionsSearch from '../../../actions/search';
import * as _actionsFilters from '../../../actions/filters';
import { withRouter } from '../../../utils/withRouter';
import { connect } from 'react-redux';
import SpinnerV2 from '../../SpinnerV2';

interface Props {
  campaigns: any[];
  isAgencyJourney?: boolean;
  showExpired?: boolean;
  groupingByClient?: boolean;
  openEditBasketModal?: (campaign: any) => void;
  onRemove?: (campaign: any) => void;
  onBasketUnlock?: (campaign: any) => void;
  actionsBasket?: typeof _actionsBasket;
  actionsSearch?: typeof _actionsSearch;
  actionsSujet: typeof _actionsSujet;
  actionsFilters: typeof _actionsFilters;
}

export enum CampaignsGroupState {
  BOOKED = 1,
  RESERVED = 2,
  SAVED = 3,
  OFFER = 4,
  EXPIRED = 5,
  LOCKED = 6,
}

const CampaignTable = ({
  campaigns,
  groupingByClient,
  isAgencyJourney,
  showExpired,
  actionsBasket,
  actionsFilters,
  actionsSearch,
  actionsSujet,
  onRemove,
  onBasketUnlock,
  openEditBasketModal,
}: Props) => {
  const [shownCampaigns, setShownCampaigns] = React.useState<any[]>([]);
  const [currentlyShownCounter, setCurrentlyShownCounter] = React.useState(0);
  const [unlockInProgress, setUnlockInProgress] = React.useState<number>(undefined);
  const intl = useIntl();
  const navigate = useNavigate();

  useEffect(() => {
    if (campaigns) {
      setShownCampaigns(campaigns.slice(0, Campaigns.SHOW_WITHOUT_BTN));
      setCurrentlyShownCounter(Campaigns.SHOW_WITHOUT_BTN);
    }
  }, [campaigns]);

  const showMore = () => {
    const newShownCampaigns = [...shownCampaigns];
    const newCurrentlyShownCounter = currentlyShownCounter + Campaigns.SHOW_WITHOUT_BTN;
    setShownCampaigns([...newShownCampaigns, ...campaigns.slice(currentlyShownCounter, newCurrentlyShownCounter)]);
    setCurrentlyShownCounter(newCurrentlyShownCounter);
  };

  const copyButton = (basketId: number, campaignNumber: number) => {
    return (
      <Button
        buttonType={EButtonType.InvertedPillMedium}
        onClick={() => {
          navigate(`/journey3/${basketId}/${campaignNumber}`);
        }}
        title={intl.formatMessage({
          id: 'ec.components.buttons.journey03.copyCampaign',
          defaultMessage: 'Kampagne nochmals buchen',
        })}
      >
        <CopyIcon />
      </Button>
    );
  };

  const detailViewButton = (campaignNumber: number) => {
    return (
      <Button
        buttonType={EButtonType.InvertedPillMedium}
        onClick={() => {
          navigate(`/campaign/${campaignNumber}`);
        }}
      >
        <RedEyeIcon />
      </Button>
    );
  };

  const editButton = (campaign: any) => {
    if (campaign?.stategroup === CampaignsGroupState.RESERVED || campaign?.stategroup === CampaignsGroupState.EXPIRED) {
      return (
        <Tooltip
          overlayClassName={'rc-tooltip-bg-white'}
          placement={'right'}
          overlay={
            <b>
              <FormattedMessage id='ec.components.buttons.journey03.openCampaignTooltip' defaultMessage='Detail' />
            </b>
          }
        >
          <div>
            <Button
              buttonType={EButtonType.InvertedPillMedium}
              onClick={() => {
                navigate(`/offerte/${campaign.number}`);
              }}
            >
              <EditIcon />
            </Button>
          </div>
        </Tooltip>
      );
    } else if (campaign?.stategroup === CampaignsGroupState.SAVED) {
      return (
        <Tooltip
          overlayClassName={'rc-tooltip-bg-white'}
          placement={'right'}
          overlay={
            <b>
              <FormattedMessage id='ec.confirmation.openBasket.legend' defaultMessage='Bearbeiten' />
            </b>
          }
        >
          <div>
            <Button
              buttonType={EButtonType.InvertedPillMedium}
              onClick={() => {
                navigate(
                  '/journey2/1?basketId=' + campaign.basket.id + '&basketreference=' + campaign.basket.basketreference,
                );
              }}
            >
              <EditIcon />
            </Button>
          </div>
        </Tooltip>
      );
    } else {
      return null;
    }
  };

  const deleteButton = (campaign: any) => {
    return (
      <Tooltip
        overlayClassName={'rc-tooltip-bg-white'}
        placement={'right'}
        overlay={
          <b>
            <FormattedMessage id='ec.confirmation.removeBasket.legend' defaultMessage='Löschen' />
          </b>
        }
      >
        <div>
          <Button
            buttonType={EButtonType.InvertedPillMedium}
            onClick={() => {
              onRemove(campaign);
            }}
          >
            <KitIcons.TrashIcon />
          </Button>
        </div>
      </Tooltip>
    );
  };

  const unlockButton = (campaign: any) => {
    return unlockInProgress === campaign.number ? (
      <div className='d-flex align-items-center justify-content-center mt-2'>
        <SpinnerV2 relative inText style={{ fill: '#B30200' }} />
      </div>
    ) : (
      <Tooltip
        overlayClassName={'rc-tooltip-bg-white'}
        placement={'right'}
        overlay={
          <b>
            <FormattedMessage
              id='ec.confirmation.lockedBasket.legend'
              defaultMessage="Warenkorb gesperrt. {newline}Mittels 'Klick' auf das Schloss-Symbol können Sie den Warenkorb entsperren und weiter bearbeiten."
              values={{ newline: <br /> }}
            />
          </b>
        }
      >
        <div>
          <Button
            buttonType={EButtonType.InvertedPillMedium}
            isDisabled={unlockInProgress === campaign.number}
            onClick={() => {
              setUnlockInProgress(campaign.number);

              actionsBasket
                .unlockBasket({
                  basket: campaign.number,
                })
                .finally(() => {
                  actionsSujet
                    .getCampaigns({
                      campaign: campaign.number,
                    })
                    .then((res) => {
                      if (res && res.length) {
                        onBasketUnlock(res[0]);
                      }
                      setUnlockInProgress(undefined);
                    });
                });
            }}
          >
            <LockIcon />
          </Button>
        </div>
      </Tooltip>
    );
  };

  const getCampaignActionButton = (campaign: any) => {
    const buttons = [];
    const { stategroup } = campaign;

    //booked
    if (stategroup === CampaignsGroupState.BOOKED) {
      buttons.push(copyButton(campaign.basket.id, campaign.number));
    }

    if (
      stategroup === CampaignsGroupState.RESERVED ||
      stategroup === CampaignsGroupState.SAVED ||
      stategroup === CampaignsGroupState.OFFER ||
      stategroup === CampaignsGroupState.EXPIRED
    ) {
      buttons.push(deleteButton(campaign));
    }

    if (stategroup === CampaignsGroupState.BOOKED || stategroup === CampaignsGroupState.RESERVED) {
      buttons.push(detailViewButton(campaign.number));
    }

    if (
      stategroup === CampaignsGroupState.SAVED ||
      stategroup === CampaignsGroupState.OFFER ||
      stategroup === CampaignsGroupState.EXPIRED
    ) {
      buttons.push(editButton(campaign));
    }

    if (stategroup === CampaignsGroupState.LOCKED) {
      buttons.push(unlockButton(campaign));
    }

    return buttons;
  };

  const getCreationOrderButtons = (campaign) => {
    const buttons = [];
    const creationOrder = +campaign.creationorder;

    if (creationOrder === -1) {
    } else if (creationOrder === 0) {
      buttons.push(
        <Button
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(
              campaign.type === 'digital'
                ? `/campaign/${campaign.number}/creation-order-new-digital`
                : `/campaign/${campaign.number}/creation-order-new`,
            );
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.designOrderIssue',
            defaultMessage: 'Gestaltungsauftrag erteilen',
          })}
        >
          <RedPlusIcon className='red-plus-icon' />
        </Button>,
      );
    } else if (creationOrder === -2) {
      buttons.push(
        <Button
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(`/campaign/${campaign.number}/production-order-railposters`);
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.designOrderIssue',
            defaultMessage: 'Gestaltungsauftrag erteilen',
          })}
        >
          <RedPlusIcon className='red-plus-icon' />
        </Button>,
      );
    } else {
      buttons.push(
        <Button
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(
              campaign.type === 'digital'
                ? `/campaign/${campaign.number}/creation-order-digital/${campaign.creationorder}`
                : `/campaign/${campaign.number}/creation-order/${campaign.creationorder}`,
            );
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.designOrderView',
            defaultMessage: 'Gestaltungsauftrag ansehen',
          })}
        >
          <GreenEyeIcon className='green-eye-icon' />
        </Button>,
      );
    }

    const url = isProd
      ? intl.formatMessage({
          id: 'ec.campaignRow.designTemplatesProdLink',
          defaultMessage: 'https://apgsga.ch/de/vorlagen-und-spezifikationen/',
        })
      : isStage
        ? intl.formatMessage({
            id: 'ec.campaignRow.designTemplatesStageLink',
            defaultMessage:
              'https://apg-website-2021-private-cloud-stage.apg.divio.cloud/de/vorlagen-und-spezifikationen/',
          })
        : intl.formatMessage({
            id: 'ec.campaignRow.designTemplatesDevLink',
            defaultMessage:
              'https://apgwebsite2021privatecloud-dev-8fd10b4.apg.divio.cloud/de/vorlagen-und-spezifikationen/',
          });

    buttons.push(
      <Button
        buttonType={EButtonType.BasicLink}
        href={url}
        title={intl.formatMessage({
          id: 'ec.campaignRow.designTemplates',
          defaultMessage: 'Gestaltungsvorlagen',
        })}
      >
        <BuyIcon />
      </Button>,
    );

    return buttons;
  };

  const getProductionOrderButtons = (campaign) => {
    const buttons = [];

    if (campaign.productionorder == -1) {
      buttons.push(
        <Button
          className={classNames(`red-close-icon`, { active: false })}
          buttonType={EButtonType.BasicLink}
          isDisabled={true}
        >
          <CloseXIcon />
        </Button>,
      );
    } else if (campaign.productionorder == 0) {
      buttons.push(
        <Button
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(`/campaign/${campaign.number}/production-order-new`);
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.printOrderIssue',
            defaultMessage: 'Druckauftrag erteilen',
          })}
        >
          <RedPlusIcon className='red-plus-icon' />
        </Button>,
      );
    } else if (campaign.productionorder == '-2') {
      buttons.push(
        <Button
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(`/campaign/${campaign.number}/production-order-railposters`);
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.printOrderIssue',
            defaultMessage: 'Druckauftrag erteilen',
          })}
        >
          <RedPlusIcon className='red-plus-icon' />
        </Button>,
      );
    } else {
      buttons.push(
        <Button
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(`/campaign/${campaign.number}/production-order/${campaign.productionorder}`);
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.printOrderView',
            defaultMessage: 'Druckauftrag ansehen',
          })}
        >
          <GreenEyeIcon className='green-eye-icon' />
        </Button>,
      );
    }

    return buttons;
  };

  const getUploadButtons = (directupload) => {
    const buttons = [];

    if (directupload && isString(directupload) && directupload.toLowerCase().includes('https')) {
      buttons.push(
        <Button
          buttonType={EButtonType.BasicLink}
          href={directupload}
          target='_blank'
          title={intl.formatMessage({
            id: 'ec.campaignRow.spotUpload',
            defaultMessage: 'Spot upload',
          })}
        >
          <UploadIcon />
        </Button>,
      );
    } else {
      buttons.push(
        <Button buttonType={EButtonType.BasicLink} isDisabled>
          <CloseXIcon />
        </Button>,
      );
    }

    return buttons;
  };

  return (
    <>
      {campaigns && campaigns.length > 0 && (
        <div className='campaign-table'>
          {shownCampaigns.map((campaign: any, index) => {
            return (
              <>
                <div
                  className={classNames('campaign-table__row', {
                    'table-row-top': index > 0,
                    expired:
                      groupingByClient && showExpired && (campaign?.stategroup == 4 || campaign?.stategroup == 5),
                  })}
                >
                  <div className='campaign-table__row__cell campaign-title'>
                    <div className='d-flex align-items-center'>
                      {campaign?.stategroup === 3 ? (
                        <div className='campaign-name-link d-flex flex-column'>
                          <span>
                            {campaign?.name}
                            <Tooltip
                              overlayClassName={'rc-tooltip-bg-white'}
                              placement={'right'}
                              overlay={
                                <b>
                                  <FormattedMessage
                                    id='ec.confirmation.renameBasket.legend'
                                    defaultMessage='Umbenennen'
                                  />
                                </b>
                              }
                            >
                              <span
                                onClick={() => {
                                  openEditBasketModal(campaign);
                                }}
                                className='cell-link'
                              >
                                <EditIcon />
                              </span>
                            </Tooltip>
                          </span>
                        </div>
                      ) : (
                        <div className='campaign-name-link d-flex flex-column'>
                          <span>{campaign?.number}</span>
                          <span>{campaign?.name}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {isAgencyJourney && !groupingByClient && (
                    <div className='campaign-table__row__cell customer'>
                      <span className='cell-text text-start'>
                        {campaign?.customer ? parse(campaign?.customer) : '-'}
                      </span>
                    </div>
                  )}

                  <div className='campaign-table__row__cell product-state'>
                    {campaign?.stategroup === CampaignsGroupState.SAVED
                      ? campaign?.campaigndate && (
                          <div className='state-pill'>
                            <span className='state-icon'>
                              <SaveIcon className='save-icon' />
                            </span>
                            <span className='state-text cell-text'>
                              {isValid(Date.parse(campaign?.campaigndate))
                                ? format(Date.parse(campaign?.campaigndate), 'dd.MM.yy', {
                                    locale: de,
                                  })
                                : campaign?.campaigndate}
                            </span>
                          </div>
                        )
                      : campaign?.statelabel && (
                          <div className='state-pill'>
                            {campaign?.state != null && (
                              <span className='state-icon'>
                                {campaign.state == 2 && <CheckedIcon className='check-icon' />}
                                {campaign.state == 51 && <ClockIcon className='clock-icon' />}
                              </span>
                            )}
                            <span className='state-text cell-text'>{campaign?.statelabel}</span>
                          </div>
                        )}
                  </div>

                  <div className='campaign-table__row__cell period'>
                    <span className='cell-header'>
                      <FormattedMessage id='ec.dashboard.table.campaign.period' defaultMessage='Zeitraum' />
                    </span>
                    <span className='cell-text'>{campaign?.timeperiod || '-'}</span>
                  </div>

                  <div className='campaign-table__row__cell format'>
                    <span className='cell-header'>
                      <FormattedMessage id='ec.dashboard.table.campaign.werbeform' defaultMessage='Werbeform' />
                    </span>
                    <span className='cell-text'>{campaign?.formats || '-'}</span>
                  </div>

                  {(isAgencyJourney || (!isAgencyJourney && campaign?.stategroup != 1)) && (
                    <>
                      <div className='campaign-table__row__cell product-count'>
                        <span className='cell-header'>
                          <FormattedMessage id='ec.dashboard.table.campaign.formats' defaultMessage='Products' />
                        </span>
                        <span className='cell-text'>{campaign?.mediacount || '-'}</span>
                      </div>

                      <div className='campaign-table__row__cell price col'>
                        <span className='cell-header'>Price</span>
                        <span className='cell-text'>
                          {campaign?.totalsum ? formatPrice(campaign.totalsum) + ' CHF' : '-'}
                        </span>
                      </div>
                    </>
                  )}

                  {!isAgencyJourney && campaign?.stategroup == 1 && (
                    <>
                      <div className='campaign-table__row__cell production-cart'>
                        <span className='cell-header'>
                          <FormattedMessage id='ec.confirmation.table.design' defaultMessage='Produktion' />
                        </span>
                        <div className='d-flex flex-row cell-link'>{getCreationOrderButtons(campaign)}</div>
                      </div>

                      <div className='campaign-table__row__cell print col'>
                        <span className='cell-header'>
                          <FormattedMessage id='ec.confirmation.table.print' defaultMessage='Druck' />
                        </span>
                        <div className='d-flex flex-row cell-link'>{getProductionOrderButtons(campaign)}</div>
                      </div>

                      <div className='campaign-table__row__cell spot-upload'>
                        <span className='cell-header'>
                          <FormattedMessage id='ec.confirmation.table.spotUpload' defaultMessage='Spot upload' />
                        </span>
                        <div className='d-flex flex-row cell-link'>{getUploadButtons(campaign?.directupload)}</div>
                      </div>
                    </>
                  )}

                  <div className='campaign-table__row__cell actions'>{getCampaignActionButton(campaign)}</div>
                </div>
              </>
            );
          })}

          {campaigns.length > currentlyShownCounter && campaigns.length !== 0 && (
            <div className='d-flex position-relative justify-content-center align-items-center py-5 campaign-table__row row table-row-top'>
              <div className='d-flex align-items-center justify-content-center'>
                <Button className='mt-0 w-auto' buttonType={EButtonType.InvertedPillMedium} onClick={showMore}>
                  <FormattedMessage id='ec.components.productsTable.showMore' defaultMessage='Mehr anzeigen' />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

function mapStateToProps(state: RootState, ownProps: any) {
  return {
    ...ownProps,
    ...state.app,
    ...state.router,
    locales: state.locales,
  };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
  return {
    actionsSujet: bindActionCreators(_actionsSujet as any, dispatch),
    actionsBasket: bindActionCreators(_actionsBasket as any, dispatch),
    actionsSearch: bindActionCreators(_actionsSearch as any, dispatch),
    actionsFilters: bindActionCreators(_actionsFilters as any, dispatch),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CampaignTable));
